import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { type ActionItem } from 'shared/ui'
import { replaceLink } from 'shared/lib'
import { type AgentActionSendMessage } from 'entities/AgentActions'
import { type Attachment } from 'entities/Attachment/model/Attachment'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type MergeField } from 'entities/MergeField/model/MergeField'
import { ShortLinkDropdownItem } from 'entities/ShortLink'
import {
  EmojiAction,
  EnumVariantMessageField,
  MessageFieldStore,
  SavedRepliesAction,
  ShortenLinkAction,
  makeSavedReplyInputAction,
  mergeFieldReplacer,
} from 'widgets/MessageField'
import { MergeFieldsIconAction } from 'widgets/MergeField/ui/actions/MergeFieldsIconAction/MergeFieldsIconAction'
import { MediaAction } from 'widgets/MessageField/ui/FieldActions/MediaAction/MediaAction'
import { SavedRepliesStore } from 'widgets/SavedReplies'
import { RequestReviewDropdownItem } from 'widgets/RequestReview'

export class AgentActionMessageStore {
  private readonly _disposeMessageReaction: IReactionDisposer | null = null
  private readonly _disposeMessageTypeReaction: IReactionDisposer | null = null
  private readonly _disposeReviewsReaction: IReactionDisposer | null = null
  private readonly _savedRepliesStore: SavedRepliesStore
  private _message: MessageFieldStore

  constructor(private _model: AgentActionSendMessage, private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._savedRepliesStore = new SavedRepliesStore(this._entities)
    this._message = new MessageFieldStore(
      {
        placeholder: 'Write a message...',
        styles: { minHeight: 106 },
        variant: EnumVariantMessageField.Default,
        showActionsItems: 4,
        resetOnUnmount: false,
        textLimit: {
          maxLength: 1600,
          message: 'Message should not be longer than 1.600 characters',
        },
        makeInputActions: (store) => [makeSavedReplyInputAction(store, this._savedRepliesStore)],
        makeActions: () => this._getActions(),
      },
      this._entities
    )

    this._disposeMessageReaction = reaction(
      () => [this._message.text, this._message.attachments] as [string, Attachment[]],
      ([text, attachments]) => {
        const prevAttachments = this._model.attachments
        const attachmentsAreSame =
          prevAttachments.length === attachments.length &&
          prevAttachments.every((attachment) => attachments.includes(attachment))

        const prevText = this._model.message
        const textIsSame = prevText === text

        if (attachmentsAreSame && textIsSame) return

        this._model.sync(text, attachments)
      }
    )

    this._disposeMessageTypeReaction = reaction(
      () => this._model.withAutoMode && this._model.isAutoMessageType,
      () => {
        if (this._model.withAutoMode && this._model.isAutoMessageType) this._message.clear()
      }
    )

    this._disposeReviewsReaction = reaction(
      () => this._entities.featureFlagsStore.reviews,
      () => {
        this._message.setActions(this._getActions())
      }
    )
  }

  private _getActions = (): ActionItem[] => {
    const actions: ActionItem[] = [
      { iconButtonComponent: <MediaAction /> },
      { iconButtonComponent: <SavedRepliesAction store={this._savedRepliesStore} useHotKey /> },
      { iconButtonComponent: <EmojiAction /> },
      {
        iconButtonComponent: (
          <MergeFieldsIconAction
            onAddMergeField={(value) => this._handleMergeField(value)}
            isAllIntegration
          />
        ),
      },
      {
        iconButtonComponent: <ShortenLinkAction />,
        dropdownItemComponent: (onCloseMenu) => (
          <ShortLinkDropdownItem
            onOpenModal={() => {
              const messageInnerText = this._message.messageInnerText
              this._entities.shortLinkStore.setMessageText(messageInnerText)
            }}
            onAddShortLink={(link) => {
              this._message.addContent && this._message.addContent(replaceLink(link) + '&nbsp;')
            }}
            onCloseMenu={onCloseMenu}
          />
        ),
      },
    ]

    if (this._entities.featureFlagsStore.reviews) {
      actions.push({ dropdownItemComponent: () => <RequestReviewDropdownItem /> })
    }

    return actions
  }

  syncMessage = () => {
    this._message.setMessageData({
      message: this._model.message,
      attachments: this._model.attachments,
      isReset: false,
      isReplace: true,
      isFocus: false,
    })
  }

  dispose = () => {
    this._disposeMessageReaction?.()
    this._disposeMessageTypeReaction?.()
    this._disposeReviewsReaction?.()
  }

  private _handleMergeField = (field: MergeField) => {
    if (!this._message.addContent) return
    const fieldContent = mergeFieldReplacer(this._entities, field.template) + '&nbsp;'
    this._message.addContent(fieldContent)
  }

  get message() {
    return this._message
  }
}
